// Cookie consent management utilities - GDPR compliant

export const COOKIE_CONSENT_KEY = 'cookieConsent';
export const COOKIE_CONSENT_DATE_KEY = 'cookieConsentDate';

// Cookie consent levels
export const CONSENT_LEVELS = {
  ESSENTIAL: 'essential',
  ALL: 'all',
  REJECTED: 'rejected'
};

// Check if user has given consent
export const hasConsent = () => {
  return localStorage.getItem(COOKIE_CONSENT_KEY) !== null;
};

// Get current consent level
export const getConsentLevel = () => {
  return localStorage.getItem(COOKIE_CONSENT_KEY) || null;
};

// Get consent date
export const getConsentDate = () => {
  const date = localStorage.getItem(COOKIE_CONSENT_DATE_KEY);
  return date ? new Date(date) : null;
};

// Set consent
export const setConsent = (level, date = new Date()) => {
  localStorage.setItem(COOKIE_CONSENT_KEY, level);
  localStorage.setItem(COOKIE_CONSENT_DATE_KEY, date.toISOString());
  
  // Apply consent immediately
  applyConsent(level);
  
  // Dispatch custom event for other components to listen to
  window.dispatchEvent(new CustomEvent('cookieConsentChanged', {
    detail: { level, date }
  }));
};

// Apply consent settings to actual cookie usage
export const applyConsent = (level) => {
  if (level === CONSENT_LEVELS.ESSENTIAL || level === CONSENT_LEVELS.ALL) {
    // Allow essential cookies
    enableEssentialCookies();
    
    if (level === CONSENT_LEVELS.ALL) {
      // Allow all cookies (for future use)
      enableAllCookies();
    } else {
      // Block non-essential cookies
      blockNonEssentialCookies();
    }
  } else if (level === CONSENT_LEVELS.REJECTED) {
    // Privacy mode: minimize all data collection
    blockNonEssentialCookies();
    enablePrivacyMode();
  }
};

// Enable essential cookies (always allowed)
export const enableEssentialCookies = () => {
  // These are necessary for site functionality
  console.log('Essential cookies enabled');
};

// Enable all cookies (with consent)
export const enableAllCookies = () => {
  console.log('All cookies enabled with consent');
  // Future: Add analytics, marketing cookies here
};

// Block non-essential cookies
export const blockNonEssentialCookies = () => {
  console.log('Non-essential cookies blocked');
  
  // Block potential tracking scripts
  if (window.gtag) {
    window.gtag = () => {};
  }
  
  // Block Facebook Pixel
  if (window.fbq) {
    window.fbq = () => {};
  }
  
  // Block other tracking functions
  if (window.dataLayer) {
    window.dataLayer = [];
  }
};

// Enable privacy mode (most restrictive option)
export const enablePrivacyMode = () => {
  console.log('Privacy mode enabled - minimizing data collection');
  
  // Disable any potential tracking
  if (window.gtag) {
    window.gtag = () => {};
  }
  
  if (window.fbq) {
    window.fbq = () => {};
  }
  
  if (window.dataLayer) {
    window.dataLayer = [];
  }
  
  // Disable console logging for privacy
  const originalLog = console.log;
  const originalWarn = console.warn;
  const originalError = console.error;
  
  console.log = () => {};
  console.warn = () => {};
  console.error = () => {};
  
  // Re-enable console after 5 seconds for debugging
  setTimeout(() => {
    console.log = originalLog;
    console.warn = originalWarn;
    console.error = originalError;
  }, 5000);
  
  // Clear any existing analytics data
  if (typeof sessionStorage !== 'undefined') {
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.includes('analytics') || key.includes('tracking') || key.includes('marketing')) {
        sessionStorage.removeItem(key);
      }
    });
  }
  
  // Set privacy headers if possible
  if (navigator.sendBeacon) {
    // Send privacy preference to server
    navigator.sendBeacon('/api/privacy-mode', JSON.stringify({
      enabled: true,
      timestamp: new Date().toISOString()
    }));
  }
};

// Check if specific consent level is granted
export const hasSpecificConsent = (requiredLevel) => {
  const currentLevel = getConsentLevel();
  
  if (requiredLevel === CONSENT_LEVELS.ESSENTIAL) {
    return currentLevel === CONSENT_LEVELS.ESSENTIAL || currentLevel === CONSENT_LEVELS.ALL;
  }
  
  if (requiredLevel === CONSENT_LEVELS.ALL) {
    return currentLevel === CONSENT_LEVELS.ALL;
  }
  
  return false;
};

// GDPR compliance check
export const isGDPRCompliant = () => {
  const consent = getConsentLevel();
  
  // Must have explicit consent
  if (!consent) return false;
  
  // Consent must be recent (within 12 months)
  if (!isConsentValid()) return false;
  
  // Must have at least essential consent
  if (consent === CONSENT_LEVELS.REJECTED) return false;
  
  return true;
};

// Get consent summary for debugging/compliance
export const getConsentSummary = () => {
  return {
    hasConsent: hasConsent(),
    consentLevel: getConsentLevel(),
    consentDate: getConsentDate(),
    isConsentValid: isConsentValid(),
    isGDPRCompliant: isGDPRCompliant(),
    timestamp: new Date().toISOString()
  };
};

// Check if consent is still valid (12 months)
export const isConsentValid = () => {
  const consentDate = getConsentDate();
  if (!consentDate) return false;
  
  const twelveMonthsAgo = new Date();
  twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);
  
  return consentDate > twelveMonthsAgo;
};

// Clear consent
export const clearConsent = () => {
  localStorage.removeItem(COOKIE_CONSENT_KEY);
  localStorage.removeItem(COOKIE_CONSENT_DATE_KEY);
  
  // Block all non-essential cookies when consent is cleared
  blockNonEssentialCookies();
  
  window.dispatchEvent(new CustomEvent('cookieConsentChanged', {
    detail: { level: null, date: null }
  }));
};
